import React, { Suspense } from 'react';
import { lazyRetry as lazy } from './utils/lazyRetry';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { ConfirmProvider } from './components/common/ConfirmationModal';
import Layout from './components/common/Layout';
import Login from './pages/Login';
import RequireEntitlement from './components/common/RequireEntitlement';
import AdminShell from './components/common/AdminShell';
import {
  loadExperimentEditor,
  loadExperiments,
  loadSessionForensics,
  loadSessionReplay,
} from './utils/editorRouteLoaders';
import {
  loadRegister,
  loadForgotPassword,
  loadResetPassword,
  loadBillingReturn,
  loadDashboard,
  loadProjects,
  loadHeatmaps,
  loadAnalytics,
  loadAnalyticsLive,
  loadTracking,
  loadSnippet,
  loadSettings,
  loadSubscriptions,
  loadClickPath,
  loadFunnels,
  loadInbox,
  loadCoreWebVitals,
  loadPulseScanDashboard,
  loadPulseScanQueue,
  loadPulseScanReport,
  loadPulseScanPageInsights,
  loadCampaigns,
  loadSeoMax,
  loadSeoMaxPageDetail,
  loadSwiss,
  loadOperationsInbox,
  loadPowerGrid,
  loadAdminGlobalDashboard,
  loadAdminClients,
  loadAdminUserDetail,
  loadAdminSupport,
  loadAdminSupportConsole,
  loadAdminTelemetry,
  loadAdminPlans,
  loadAdminServicePackages,
  loadAdminExperiments,
  loadAdminProjects,
  loadAdminAiSettings,
  loadAdminCommerce,
  loadAdminDataPlatform,
  loadAdminInbox,
  loadAdminBackupRestore,
  loadAdminLogAnalytics,
  loadAdminLayers,
  loadAdminHoldoutReport,
  loadAdminMetrics,
  loadAdminStatisticalDefaults,
} from './utils/shellRouteLoaders';

const Register = lazy(loadRegister);
const ForgotPassword = lazy(loadForgotPassword);
const ResetPassword = lazy(loadResetPassword);
const BillingReturn = lazy(loadBillingReturn);
const ExperimentEditor = lazy(loadExperimentEditor);
const Experiments = lazy(loadExperiments);
const SessionReplay = lazy(loadSessionReplay);
const SessionForensics = lazy(loadSessionForensics);
const Dashboard = lazy(loadDashboard);
const Projects = lazy(loadProjects);
const Heatmaps = lazy(loadHeatmaps);
const Analytics = lazy(loadAnalytics);
const AnalyticsLive = lazy(loadAnalyticsLive);
const Tracking = lazy(loadTracking);
const Snippet = lazy(loadSnippet);
const Settings = lazy(loadSettings);
const Subscriptions = lazy(loadSubscriptions);
const ClickPath = lazy(loadClickPath);
const Funnels = lazy(loadFunnels);
const Inbox = lazy(loadInbox);
const CoreWebVitals = lazy(loadCoreWebVitals);
const PulseScanDashboard = lazy(loadPulseScanDashboard);
const PulseScanQueue = lazy(loadPulseScanQueue);
const PulseScanReport = lazy(loadPulseScanReport);
const PulseScanPageInsights = lazy(loadPulseScanPageInsights);
const Campaigns = lazy(loadCampaigns);
const SeoMax = lazy(loadSeoMax);
const SeoMaxPageDetail = lazy(loadSeoMaxPageDetail);
const Swiss = lazy(loadSwiss);
const OperationsInbox = lazy(loadOperationsInbox);
const PowerGrid = lazy(loadPowerGrid);
const AdminGlobalDashboard = lazy(loadAdminGlobalDashboard);
const AdminClients = lazy(loadAdminClients);
const AdminUserDetail = lazy(loadAdminUserDetail);
const AdminSupport = lazy(loadAdminSupport);
const AdminSupportConsole = lazy(loadAdminSupportConsole);
const AdminTelemetry = lazy(loadAdminTelemetry);
const AdminPlans = lazy(loadAdminPlans);
const AdminServicePackages = lazy(loadAdminServicePackages);
const AdminExperiments = lazy(loadAdminExperiments);
const AdminProjects = lazy(loadAdminProjects);
const AdminAiSettings = lazy(loadAdminAiSettings);
const AdminCommerce = lazy(loadAdminCommerce);
const AdminDataPlatform = lazy(loadAdminDataPlatform);
const AdminInbox = lazy(loadAdminInbox);
const AdminBackupRestore = lazy(loadAdminBackupRestore);
const AdminLogAnalytics = lazy(loadAdminLogAnalytics);
const AdminLayers = lazy(loadAdminLayers);
const AdminHoldoutReport = lazy(loadAdminHoldoutReport);
const AdminMetrics = lazy(loadAdminMetrics);
const AdminStatisticalDefaults = lazy(loadAdminStatisticalDefaults);

/**
 * Legacy route redirect that preserves query string + hash so existing
 * deep links continue to work after the /analytics/* nav restructure.
 */
function LegacyRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

const pageFallback = (
  <div className="flex justify-center py-16">
    <div className="w-8 h-8 border-2 border-navy-700/30 border-t-navy-700 rounded-full animate-spin" />
  </div>
);

function Lazy({ children }) {
  return <Suspense fallback={pageFallback}>{children}</Suspense>;
}

export default function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <ConfirmProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#1d1d1f',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              fontSize: '15px',
            },
          }}
        />
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Lazy><Register /></Lazy>} />
          <Route path="/forgot-password" element={<Lazy><ForgotPassword /></Lazy>} />
          <Route path="/reset-password" element={<Lazy><ResetPassword /></Lazy>} />
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Lazy><Dashboard /></Lazy>} />
            <Route path="/projects" element={<Lazy><Projects /></Lazy>} />
            <Route path="/projects/:id" element={<Lazy><Projects /></Lazy>} />
            <Route path="/experiments" element={<RequireEntitlement service="ab_testing"><Lazy><Experiments /></Lazy></RequireEntitlement>} />
            <Route path="/experiments/new" element={<RequireEntitlement service="ab_testing"><Lazy><ExperimentEditor /></Lazy></RequireEntitlement>} />
            <Route path="/experiments/:id/edit" element={<RequireEntitlement service="ab_testing"><Lazy><ExperimentEditor /></Lazy></RequireEntitlement>} />
            <Route path="/experiments/:id/clone" element={<RequireEntitlement service="ab_testing"><Lazy><ExperimentEditor /></Lazy></RequireEntitlement>} />
            <Route path="/heatmaps" element={<LegacyRedirect to="/analytics/heatmaps" />} />
            <Route path="/analytics" element={<RequireEntitlement anyOf={['heatmaps', 'ab_testing', 'cwv', 'campaigns']}><Lazy><Analytics /></Lazy></RequireEntitlement>} />
            <Route
              path="/analytics/live"
              element={
                <RequireEntitlement anyOf={['heatmaps', 'ab_testing', 'cwv', 'campaigns']}>
                  <Lazy><AnalyticsLive /></Lazy>
                </RequireEntitlement>
              }
            />
            <Route path="/analytics/olap" element={<LegacyRedirect to="/analytics/live" />} />
            <Route
              path="/analytics/funnels"
              element={
                <RequireEntitlement anyOf={['clickpath', 'heatmaps', 'ab_testing']}>
                  <Lazy><Funnels /></Lazy>
                </RequireEntitlement>
              }
            />
            <Route
              path="/analytics/powergrid"
              element={
                <RequireEntitlement anyOf={['heatmaps', 'ab_testing', 'cwv']}>
                  <Lazy><PowerGrid /></Lazy>
                </RequireEntitlement>
              }
            />
            <Route
              path="/analytics/heatmaps"
              element={<RequireEntitlement service="heatmaps"><Lazy><Heatmaps /></Lazy></RequireEntitlement>}
            />
            <Route
              path="/analytics/sessions"
              element={
                <RequireEntitlement anyOf={['heatmaps', 'clickpath']}>
                  <Lazy><SessionReplay /></Lazy>
                </RequireEntitlement>
              }
            />
            <Route path="/tracking" element={<RequireEntitlement anyOf={['heatmaps', 'ab_testing']}><Lazy><Tracking /></Lazy></RequireEntitlement>} />
            <Route path="/campaigns" element={<Lazy><Campaigns /></Lazy>} />
            <Route path="/seo-max" element={<Lazy><SeoMax /></Lazy>} />
            <Route
              path="/seo-max/pages/:pageId"
              element={
                <RequireEntitlement service="seomax">
                  <Lazy><SeoMaxPageDetail /></Lazy>
                </RequireEntitlement>
              }
            />
            <Route path="/snippet" element={<Lazy><Snippet /></Lazy>} />
            <Route path="/core-web-vitals" element={<RequireEntitlement service="cwv"><Lazy><CoreWebVitals /></Lazy></RequireEntitlement>} />
            <Route path="/pulsescan" element={<LegacyRedirect to="/pulsescan/dashboard" />} />
            <Route path="/pulsescan/dashboard" element={<RequireEntitlement service="cwv"><Lazy><PulseScanDashboard /></Lazy></RequireEntitlement>} />
            <Route path="/pulsescan/queue" element={<RequireEntitlement service="cwv"><Lazy><PulseScanQueue /></Lazy></RequireEntitlement>} />
            <Route path="/pulsescan/page-insights" element={<RequireEntitlement service="cwv"><Lazy><PulseScanPageInsights /></Lazy></RequireEntitlement>} />
            <Route path="/pulsescan/report/:runId" element={<RequireEntitlement service="cwv"><Lazy><PulseScanReport /></Lazy></RequireEntitlement>} />
            <Route path="/inbox" element={<Lazy><Inbox /></Lazy>} />
            <Route path="/settings" element={<Lazy><Settings /></Lazy>} />
            <Route path="/subscriptions" element={<Lazy><Subscriptions /></Lazy>} />
            <Route path="/billing/return" element={<Lazy><BillingReturn /></Lazy>} />
            <Route path="/click-path" element={<RequireEntitlement service="clickpath"><Lazy><ClickPath /></Lazy></RequireEntitlement>} />
            <Route path="/funnels" element={<LegacyRedirect to="/analytics/funnels" />} />
            <Route path="/session-replay" element={<LegacyRedirect to="/analytics/sessions" />} />
            <Route
              path="/session-forensics"
              element={
                <RequireEntitlement service="clickpath">
                  <Lazy><SessionForensics /></Lazy>
                </RequireEntitlement>
              }
            />
            <Route path="/swiss" element={<RequireEntitlement service="swiss"><Lazy><Swiss /></Lazy></RequireEntitlement>} />
            <Route path="/operations" element={<Lazy><OperationsInbox /></Lazy>} />
            <Route path="/power-grid" element={<LegacyRedirect to="/analytics/powergrid" />} />
          </Route>
          <Route path="/admin" element={<AdminShell />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<Lazy><AdminGlobalDashboard /></Lazy>} />
            <Route path="overview" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="settings" element={<Lazy><Settings /></Lazy>} />
            <Route path="clients" element={<Lazy><AdminClients /></Lazy>} />
            <Route path="clients/:id" element={<Lazy><AdminUserDetail /></Lazy>} />
            <Route path="users" element={<Navigate to="/admin/clients" replace />} />
            <Route path="users/:id" element={<Lazy><AdminUserDetail /></Lazy>} />
            <Route path="experiments" element={<Lazy><AdminExperiments /></Lazy>} />
            <Route path="layers" element={<Lazy><AdminLayers /></Lazy>} />
            <Route path="holdout-report" element={<Lazy><AdminHoldoutReport /></Lazy>} />
            <Route path="metrics" element={<Lazy><AdminMetrics /></Lazy>} />
            <Route path="statistical-defaults" element={<Lazy><AdminStatisticalDefaults /></Lazy>} />
            <Route path="projects" element={<Lazy><AdminProjects /></Lazy>} />
            <Route path="support" element={<Lazy><AdminSupportConsole /></Lazy>} />
            <Route path="support/tickets/:ticketId" element={<Lazy><AdminSupport /></Lazy>} />
            <Route path="support/:ticketId" element={<Lazy><AdminSupport /></Lazy>} />
            <Route path="telemetry" element={<Lazy><AdminTelemetry /></Lazy>} />
            <Route path="plans" element={<Lazy><AdminPlans /></Lazy>} />
            <Route path="service-packages" element={<Lazy><AdminServicePackages /></Lazy>} />
            <Route path="ai-settings" element={<Lazy><AdminAiSettings /></Lazy>} />
            <Route path="commerce" element={<Lazy><AdminCommerce /></Lazy>} />
            <Route path="data-platform" element={<Lazy><AdminDataPlatform /></Lazy>} />
            <Route path="inbox" element={<Lazy><AdminInbox /></Lazy>} />
            <Route path="backup-restore" element={<Lazy><AdminBackupRestore /></Lazy>} />
            <Route path="log-analytics" element={<Lazy><AdminLogAnalytics /></Lazy>} />
          </Route>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
